import { observable, action, computed, runInAction, makeObservable } from "mobx";
import { ListStore } from "Stores/RESTStore/ListStore";
import { NewStore } from "Stores/RESTStore/NewStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { EditStore } from "Stores/RESTStore/EditStore";
import { DataStore } from "Stores/RESTStore/DataStore";
import { API } from "General/API";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { groupMemberStore } from "Pages/Project/Groups/Stores/GroupMembers";
import { stringSort } from "utils/defaultSort";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { TransmittedMember } from "./GroupMembers";
import { ViewUIFilters } from "Pages/Project/Views/Pages/View";

export const ALL_MEMBERS = -108;

class GroupsListStore extends ListStore<API_Group> {
  searchValue: string = "";
  searchByTypeValue: string[] = [];
  groupsForAccessRights: API_Group[] = [];

  constructor() {
    super();

    makeObservable(this, {
      groupsForAccessRights: observable,
      systemGroups: computed,
      publicGroups: computed,
      publicGroupsUnfiltered: computed,
      privateGroups: computed,
      organisationsGroups: computed,
      cloneableGroups: computed,
      forMailAndPlot: computed,
      forRights: computed,
      forRightsOnly: computed,
      forRightsOnlyWithoutSubGroups: computed,
      fetchGroupsOfTemplate: action,
      searchValue: observable,
      searchByTypeValue: observable,
      handleSearch: action,
      handleFilterByType: action,
    });
  }

  url(): string {
    return API_URL.GROUPS.getOrganisationsOfProject(projectsDataStore.projectId);
  }

  async fetch(filter?) {
    await Promise.all([super.fetch(filter), this.fetchGroupsForAccessRights()]);
  }

  async fetchGroupsForAccessRights() {
    const response = await API.getData<API_Group[]>(
      API_URL.GROUPS.getGroupsWithoutOrganisations(projectsDataStore.projectId)
    );
    runInAction(() => {
      this.groupsForAccessRights = response;
    });
  }

  @handleStoreState()
  async fetchGroupsOfTemplate(filter?: {}) {
    const items = await API.getData(
      API_URL.ADMIN_TEMPLATES.getGroupsOfTemplateProject(projectsDataStore.projectId),
      filter
    );

    runInAction(() => {
      if (Array.isArray(items)) {
        this.list.replace([...items]);
      }
    });
  }

  handleSearch(searchValue: string) {
    this.searchValue = searchValue;
  }

  handleFilterByType(activeFilters: ViewUIFilters) {
    this.searchByTypeValue = activeFilters.type;
  }

  getMember(group: API_Group, user: API_Contact & API_User): API_GroupMember {
    return user.contactType !== undefined
      ? group.groupMembers.find((m) => m.id === user.id && m.memberType === "GLOBAL_CONTACT")
      : group.groupMembers.find((m) => m.id === user.id && m.memberType === "PROJECT_MEMBER");
  }

  getParentGroups(group: API_Group) {
    return this.list.filter((g) => g.subGroupIds.includes(group.id));
  }

  getGroupsByIds(ids: number[]) {
    return this.list.filter((g) => ids.find((id) => id === g.id));
  }

  getGroupNamesByIds(ids: number[]) {
    return this.getGroupsByIds(ids).map((g) => g.name);
  }

  searchFilter(group: API_Group): boolean {
    if (this.searchValue === "" && !this.searchByTypeValue) return true;

    if (this.searchByTypeValue?.length === 1) {
      if (this.searchByTypeValue[0] === "mailplot" && !group.forMailAndPlot) return false;
      if (this.searchByTypeValue[0] === "rights" && !group.forSecurity) return false;
    }
    const searchVal = this.searchValue?.toLowerCase();

    if (!searchVal) return true;

    const isNameMatched = group.name.toLowerCase().includes(searchVal);

    const isGroupUserMatched = group.groupMembers.some((member: API_GroupMember) =>
      [member.firstName, member.surname, member.company].some((field) => field?.toLowerCase().includes(searchVal))
    );

    return isNameMatched || isGroupUserMatched;
  }

  get systemGroups() {
    return this.list.filter((val) => val.type === "SYSTEM" && this.searchFilter(val));
  }

  get publicGroups() {
    return this.list.filter((val) => val.type === "PUBLIC" && this.searchFilter(val));
  }
  get publicGroupsUnfiltered() {
    return this.list.filter((val) => val.type === "PUBLIC");
  }

  get privateGroups() {
    return this.list.filter((val) => val.type === "PRIVATE" && this.searchFilter(val));
  }

  get organisationsGroups() {
    return this.list.filter((val) => val.type === "ORGANISATION" && this.searchFilter(val));
  }

  get publicAndOrganisation() {
    return this.list.filter((val) => val.type === "ORGANISATION" || val.type === "PUBLIC");
  }

  get cloneableGroups() {
    return this.list
      .filter((val) => val.type === "PUBLIC" || val.type === "PRIVATE")
      .sort((a, b) => stringSort(a, b, "name"));
  }

  get forMailAndPlot() {
    return this.list.filter((group) => group.forMailAndPlot);
  }

  get forRights() {
    return this.list.filter((group) => group.forSecurity);
  }

  get forRightsOnly() {
    return this.list.filter((group) => group.forSecurity && !group.forMailAndPlot);
  }
  get forRightsOnlyWithoutSubGroups() {
    return this.list.filter(
      (group) => group.type === "PUBLIC" && group.forSecurity && !group.forMailAndPlot && group.subGroupIds.length === 0
    );
  }
}

export const groupListStore = new GroupsListStore();
class NewGroupStore extends NewStore<API_Group> {
  defaultData: API_Group = {
    type: "PRIVATE",
    forMailAndPlot: true,
  };

  url(): string {
    return API_URL.GROUPS.createGroup(projectsDataStore.projectId);
  }
}
export const newGroupStore = new NewGroupStore(groupListStore);

class DataGroupStore extends DataStore<API_Group> {
  url(id: number) {
    return API_URL.GROUPS.getGroup(projectsDataStore.projectId, id);
  }
}

export const dataGroupStore = new DataGroupStore();

export class EditGroupStore extends EditStore<API_Group> {
  constructor(listStore, identifier) {
    super(listStore, identifier);
    makeObservable(this, {
      editGroup: action.bound,
    });
  }
  url(id: number) {
    return API_URL.GROUPS.editGroup(projectsDataStore.projectId, id);
  }

  @handleStoreState()
  async editGroup(v: API_Group) {
    try {
      const response: API_Group = await API.editData(this.url(this.data[this.identifier]), v);
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export const editGroupStore = new EditGroupStore(dataGroupStore, groupListStore);

class GroupGUIStore {
  newGroupVisible: boolean = false;
  editGroupVisible: boolean = false;
  copyGroupVisible: boolean = false;
  groupMatrixVisible: boolean = false;
  currentGroup: API_Group = null;
  currentGroupId: number = ALL_MEMBERS;

  constructor() {
    makeObservable(this, {
      newGroupVisible: observable,
      editGroupVisible: observable,
      copyGroupVisible: observable,
      groupMatrixVisible: observable,
      currentGroup: observable,
      currentGroupId: observable,
      setGroupId: action.bound,
      setGroup: action.bound,
      clear: action,
      addSubGroupsToCurrent: action,
      removeSubGroupsFromCurrent: action,
      isEditable: computed,
      toggleNewGroupModal: action.bound,
      toggleEditGroupModal: action.bound,
      toggleCopyGroupModal: action.bound,
      toggleGroupMatrixModal: action.bound,
    });

    this.newGroupVisible = false;
    this.editGroupVisible = false;
    this.copyGroupVisible = false;
    this.groupMatrixVisible = false;
  }

  clear() {
    this.currentGroup = undefined;
  }

  setGroupId(id?: number) {
    if (id === undefined) {
      id = ALL_MEMBERS;
    }
    if (typeof id === "string") {
      id = parseInt(id, 10);
    }

    this.currentGroupId = id;
    if (groupListStore.list.length) {
      this.setGroup();
    }
  }

  setGroup() {
    const group = groupListStore.getItem(this.currentGroupId);
    this.currentGroup = group;
  }

  addSubGroupsToCurrent(memberList: TransmittedMember[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    memberList.forEach((m) => this.currentGroup.subGroupIds.push(m as any));
  }

  removeSubGroupsFromCurrent(subGroupId: number) {
    const idx = this.currentGroup.subGroupIds.findIndex((id) => subGroupId === id);
    if (idx > -1) {
      this.currentGroup.subGroupIds.splice(idx, 1);
    }
  }

  get isEditable() {
    const rightsMap = {
      PRIVATE: () => true,
      PUBLIC: () => projectsDataStore.isAdmin,
      SYSTEM: () => false,
      default: () => false,
    };

    if (this.currentGroup) {
      return (rightsMap[this.currentGroup.type] || rightsMap.default)();
    } else {
      return false;
    }
  }

  toggleNewGroupModal() {
    this.newGroupVisible = !this.newGroupVisible;
    if (!this.newGroupVisible) {
      newGroupStore.clear();
    }
  }

  toggleGroupMatrixModal() {
    this.groupMatrixVisible = !this.groupMatrixVisible;
  }

  toggleEditGroupModal() {
    this.editGroupVisible = !this.editGroupVisible;
    if (this.editGroupVisible) {
      editGroupStore.dataStore.fetch(this.currentGroupId);
    }
  }
  toggleCopyGroupModal() {
    this.copyGroupVisible = !this.copyGroupVisible;
    if (this.copyGroupVisible) {
      editGroupStore.dataStore.fetch(this.currentGroupId);
    }
  }
}

export const groupGUIStore = new GroupGUIStore();

class GroupAddMember {
  constructor() {
    makeObservable(this, {
      addMember: action.bound,
    });
  }

  url() {
    return API_URL.GROUPS.assignMembersToGroup(projectsDataStore.projectId, groupGUIStore.currentGroupId);
  }

  async addMember(formData: TransmittedMember[]) {
    try {
      const response: API_ViewInformation[] = await API.newData(this.url(), formData);
      if (response) {
        runInAction(() => {
          groupMemberStore.addMembers(formData);
        });
      }
    } catch (error) {
      throw error;
    }
  }
}

export const groupAddMember = new GroupAddMember();

const handleRemoveMember = async (
  member: API_GroupMember,
  groupID: number = groupGUIStore.currentGroupId,
  updateMemberStore = true
) => {
  try {
    if (member.memberType === "PROJECT_MEMBER") {
      await API.deleteData(API_URL.GROUPS.unassignUserFromGroup(projectsDataStore.projectId, groupID, member.id));
    } else {
      await API.deleteData(API_URL.GROUPS.unassignContactFromGroup(projectsDataStore.projectId, groupID, member.id));
    }
    if (updateMemberStore) {
      groupMemberStore.removeMember(member);
    }
  } catch (err) {
    notificationError("Error", "Could not remove Member from group.");
  }
};

export { handleRemoveMember };

class GroupAddGroupMember {
  constructor() {
    makeObservable(this, {
      addMember: action.bound,
    });
  }

  url() {
    return API_URL.GROUPS.assignGroupsToGroup(projectsDataStore.projectId, groupGUIStore.currentGroupId);
  }

  async addMember(formData: TransmittedMember[]) {
    try {
      const response: API_ViewInformation[] = await API.newData(this.url(), formData);
      if (response) {
        runInAction(() => {
          groupMemberStore.addGroupMembers(formData);
        });
      }
    } catch (error) {
      throw error;
    }
  }
}

export const groupAddGroupMember = new GroupAddGroupMember();

const handleRemoveGroupsMember = async (groupID: number = groupGUIStore.currentGroupId, subGroupId: number) => {
  try {
    await API.deleteData(API_URL.GROUPS.unassignGroupFromGroup(projectsDataStore.projectId, groupID, subGroupId));
    groupMemberStore.removeGroupMember(subGroupId);
  } catch (err) {
    notificationError("Error", "Could not remove group from group.");
  }
};

export { handleRemoveGroupsMember };
